import {
  ArrowLeftRight,
  Building2,
  ChevronDown,
  FileText,
  Package,
  PiggyBank,
  Plus,
  Receipt,
  SlidersHorizontal,
  Store,
  Truck,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/shared/lib/cn';

interface Action {
  to: string;
  icon: LucideIcon;
  key: string;
  fallback: string;
  tone?: string;
}
interface Group {
  titleKey: string;
  titleFallback: string;
  items: Action[];
}

const GROUPS: Group[] = [
  {
    titleKey: 'quick.g_sales',
    titleFallback: 'المبيعات',
    items: [
      { to: '/app/sales/new', icon: Receipt, key: 'quick.new_sale', fallback: 'بيع جديد', tone: 'text-success' },
      { to: '/app/pos', icon: Store, key: 'quick.pos', fallback: 'نقطة البيع', tone: 'text-primary' },
      { to: '/app/quotations/new', icon: FileText, key: 'quick.new_quote', fallback: 'عرض سعر' },
    ],
  },
  {
    titleKey: 'quick.g_purchases',
    titleFallback: 'المشتريات',
    items: [
      { to: '/app/purchases/new', icon: Truck, key: 'quick.new_purchase', fallback: 'فاتورة شراء', tone: 'text-primary' },
    ],
  },
  {
    titleKey: 'quick.g_inventory',
    titleFallback: 'المخزون',
    items: [
      { to: '/app/products/new', icon: Package, key: 'quick.new_product', fallback: 'إضافة منتج' },
      { to: '/app/transfers/new', icon: ArrowLeftRight, key: 'quick.new_transfer', fallback: 'تحويل مخزون' },
      { to: '/app/adjustments/new', icon: SlidersHorizontal, key: 'quick.new_adjustment', fallback: 'تسوية مخزون' },
    ],
  },
  {
    titleKey: 'quick.g_people',
    titleFallback: 'الأشخاص',
    items: [
      { to: '/app/customers/new', icon: UserPlus, key: 'quick.new_customer', fallback: 'إضافة عميل' },
      { to: '/app/suppliers', icon: Building2, key: 'quick.new_supplier', fallback: 'إضافة مورّد' },
    ],
  },
  {
    titleKey: 'quick.g_finance',
    titleFallback: 'المالية',
    items: [
      { to: '/app/expenses/new', icon: Wallet, key: 'quick.new_expense', fallback: 'إضافة مصروف', tone: 'text-warning' },
      { to: '/app/deposits/new', icon: PiggyBank, key: 'quick.new_deposit', fallback: 'إيداع', tone: 'text-success' },
    ],
  },
];

export function QuickCreateMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="h-9 gap-1.5" aria-label={t('quick.quick_actions', 'إجراءات سريعة')}>
          <Plus className="h-4 w-4" />
          <span className="hidden md:inline">{t('quick.create', 'إضافة')}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[80vh] w-60 overflow-y-auto rounded-xl">
        {GROUPS.map((group, gi) => (
          <div key={group.titleKey}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t(group.titleKey, group.titleFallback)}
            </DropdownMenuLabel>
            {group.items.map(({ to, icon: Icon, key, fallback, tone }) => (
              <DropdownMenuItem
                key={to}
                onClick={() => navigate(to)}
                className="cursor-pointer gap-2 rounded-lg py-2"
              >
                <Icon className={cn('h-4 w-4', tone ?? 'text-muted-foreground')} />
                {t(key, fallback)}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
